import { useEffect, useState } from 'react';
import { Mail, Send, Trash2, ShieldCheck, Info, AlertTriangle } from 'lucide-react';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { destinatariosInvalidos, formatearDestinatarios, parsearDestinatarios } from '@shared/email';

/**
 * Sprint 36 — Configuración SMTP cifrada (safeStorage/DPAPI).
 *
 * Permite al dueño configurar las credenciales del servidor de correo
 * que se usan para enviar reportes de errores (errorReporter) y el
 * email de cierre del día.
 *
 * Las credenciales NO viajan al renderer una vez guardadas; solo el
 * estado configurado/no.
 */
interface EstadoSmtp {
  configurado: boolean;
  user: string | null;
  host: string | null;
}

export function SmtpForm() {
  const [estado, setEstado] = useState<EstadoSmtp | null>(null);
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [host, setHost] = useState('smtp.gmail.com');
  const [puerto, setPuerto] = useState(587);
  const [destino, setDestino] = useState('');
  const [accion, setAccion] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<{ tipo: 'ok' | 'error'; texto: string } | null>(null);
  const [consentimiento, setConsentimiento] = useState(false);

  // Sprint 57 — el destino admite varias cuentas separadas por coma.
  const listaDestinos = parsearDestinatarios(destino);
  const correosMalEscritos = destinatariosInvalidos(destino);

  const cargar = async () => {
    const [est, dst, cons] = await Promise.all([
      window.api.smtp.estado(),
      window.api.config.obtener('email_soporte'),
      window.api.config.obtener('telemetria_consentimiento'),
    ]);
    if (est.ok) {
      setEstado(est.data);
      if (est.data.user) setUser(est.data.user);
      if (est.data.host) setHost(est.data.host);
    }
    if (dst.ok && dst.data) setDestino(dst.data);
    if (cons.ok) setConsentimiento(cons.data === 'true');
  };

  useEffect(() => {
    cargar();
  }, []);

  const guardarCredenciales = async () => {
    setMensaje(null);
    if (!user.trim() || !password) {
      setMensaje({ tipo: 'error', texto: 'Usuario y contraseña son obligatorios.' });
      return;
    }
    setAccion('guardar');
    const res = await window.api.smtp.guardar({
      user: user.trim(),
      password,
      host: host.trim() || undefined,
      port: Number.isFinite(puerto) ? puerto : undefined,
    });
    setAccion(null);
    if (res.ok) {
      setPassword(''); // limpiar password del input por seguridad
      setMensaje({ tipo: 'ok', texto: 'Credenciales SMTP guardadas correctamente.' });
      cargar();
    } else {
      setMensaje({ tipo: 'error', texto: traducirErrorBackend(res.error, res.codigo) });
    }
  };

  const guardarDestino = async () => {
    // Sprint 57 — no dejamos guardar un correo mal escrito: el reporte se
    // perdería en silencio el día que de verdad haga falta.
    if (correosMalEscritos.length > 0) {
      setMensaje({
        tipo: 'error',
        texto: `Revisa estas direcciones: ${correosMalEscritos.join(', ')}`,
      });
      return;
    }
    setAccion('destino');
    setMensaje(null);
    // Se guarda normalizado (sin espacios sobrantes ni repetidos).
    const r1 = await window.api.config.actualizar('email_soporte', formatearDestinatarios(destino));
    const r2 = await window.api.config.actualizar(
      'telemetria_consentimiento',
      consentimiento ? 'true' : 'false',
    );
    setAccion(null);
    if (r1.ok && r2.ok) {
      setMensaje({ tipo: 'ok', texto: 'Configuración de destinatario guardada.' });
    } else {
      setMensaje({ tipo: 'error', texto: 'No se pudo guardar el destinatario.' });
    }
  };

  const probar = async () => {
    setMensaje(null);
    if (!user.trim() || !password || listaDestinos.length === 0) {
      setMensaje({
        tipo: 'error',
        texto: 'Para probar necesitas: usuario, contraseña y email destino.',
      });
      return;
    }
    if (correosMalEscritos.length > 0) {
      setMensaje({
        tipo: 'error',
        texto: `Revisa estas direcciones: ${correosMalEscritos.join(', ')}`,
      });
      return;
    }
    setAccion('probar');
    const res = await window.api.smtp.probar({
      user: user.trim(),
      password,
      host: host.trim() || undefined,
      port: Number.isFinite(puerto) ? puerto : undefined,
      to: formatearDestinatarios(destino),
    });
    setAccion(null);
    if (res.ok) {
      setMensaje({
        tipo: 'ok',
        texto: `✓ Correo de prueba enviado a ${listaDestinos.join(', ')}. Revisa la bandeja.`,
      });
    } else {
      setMensaje({ tipo: 'error', texto: traducirErrorBackend(res.error, res.codigo) });
    }
  };

  const borrar = async () => {
    const ok = await window.confirmar({
      titulo: '¿Borrar credenciales SMTP?',
      mensaje: 'Se eliminarán las credenciales cifradas guardadas. Los reportes automáticos no se enviarán hasta que las configures de nuevo.',
      textoConfirmar: 'Sí, borrar',
      variant: 'destructive',
    });
    if (!ok) return;
    setAccion('borrar');
    const res = await window.api.smtp.borrar();
    setAccion(null);
    if (res.ok) {
      setUser('');
      setPassword('');
      setHost('smtp.gmail.com');
      setMensaje({ tipo: 'ok', texto: 'Credenciales SMTP borradas.' });
      cargar();
    } else {
      setMensaje({ tipo: 'error', texto: traducirErrorBackend(res.error, res.codigo) });
    }
  };

  return (
    <div className="max-w-2xl space-y-5">
      <header className="flex items-start gap-3">
        <div className="rounded-full bg-blue-100 p-2 shrink-0">
          <Mail className="h-5 w-5 text-blue-700" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Email / Soporte</h2>
          <p className="text-sm text-slate-600 mt-1">
            Configura el correo desde el que se envían reportes automáticos. Las
            credenciales se guardan cifradas en este equipo (DPAPI de Windows).
          </p>
        </div>
      </header>

      {estado?.configurado && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-900 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" />
          SMTP ya configurado con <strong>{estado.user}</strong>
          {estado.host && <span className="text-emerald-700">· {estado.host}</span>}
        </div>
      )}

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-sm text-amber-900">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">¿Usas Gmail?</p>
          <p className="text-amber-800 mt-1">
            Gmail ya no acepta tu contraseña normal. Tienes que crear una
            <strong> contraseña de aplicación</strong> en tu cuenta Google
            (Seguridad → Verificación en dos pasos → Contraseñas de aplicaciones).
            Pega esa contraseña de 16 letras en el campo "Contraseña" de abajo.
          </p>
        </div>
      </div>

      {/* Credenciales SMTP */}
      <div className="card space-y-3">
        <h3 className="font-semibold text-slate-800">Servidor de correo</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor="smtp-user">Usuario (email remitente)</label>
            <input
              id="smtp-user"
              className="input"
              value={user}
              onChange={(e) => setUser(e.target.value)}
              placeholder="tu-correo@gmail.com"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="label" htmlFor="smtp-pass">Contraseña</label>
            <input
              id="smtp-pass"
              type="password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={estado?.configurado ? '(guardada — escribe para cambiar)' : ''}
              autoComplete="current-password"
            />
          </div>
          <div>
            <label className="label" htmlFor="smtp-host">Servidor SMTP</label>
            <input
              id="smtp-host"
              className="input"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="smtp.gmail.com"
            />
          </div>
          <div>
            <label className="label" htmlFor="smtp-port">Puerto</label>
            <input
              id="smtp-port"
              type="number"
              className="input"
              value={puerto}
              onChange={(e) => setPuerto(Number(e.target.value) || 587)}
              min={1}
              max={65535}
            />
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={guardarCredenciales} disabled={accion === 'guardar'} className="btn-primary">
            {accion === 'guardar' ? 'Guardando…' : 'Guardar credenciales'}
          </button>
          {estado?.configurado && (
            <button onClick={borrar} disabled={accion === 'borrar'} className="btn-ghost text-red-700">
              <Trash2 className="h-4 w-4" />
              Borrar credenciales
            </button>
          )}
        </div>
      </div>

      {/* Destinatario y consentimiento */}
      <div className="card space-y-3">
        <h3 className="font-semibold text-slate-800">¿A dónde llegan los reportes?</h3>
        <div>
          <label className="label" htmlFor="smtp-dest">Email destino</label>
          <input
            id="smtp-dest"
            type="email"
            // Sprint 57 — `multiple` permite varias cuentas separadas por coma;
            // sin él el navegador marca inválido un segundo destinatario.
            multiple
            className="input"
            value={destino}
            onChange={(e) => setDestino(e.target.value)}
            placeholder="soporte@miempresa.com, respaldo@miempresa.com"
            autoComplete="email"
          />
          <p className="text-xs text-slate-500 mt-1">
            Aquí llegarán los reportes de errores. Puedes poner{' '}
            <strong>varias cuentas separadas por coma</strong>.
          </p>
          {correosMalEscritos.length > 0 && (
            <p className="text-xs text-orange-700 mt-1">
              Revisa estas direcciones: {correosMalEscritos.join(', ')}
            </p>
          )}
          {correosMalEscritos.length === 0 && listaDestinos.length > 1 && (
            <p className="text-xs text-slate-500 mt-1">
              Se enviará a {listaDestinos.length} cuentas.
            </p>
          )}
        </div>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={consentimiento}
            onChange={(e) => setConsentimiento(e.target.checked)}
            className="mt-1"
          />
          <span className="text-sm text-slate-700">
            Acepto que el sistema envíe automáticamente reportes de errores al email
            destino para diagnosticar problemas. Los reportes no incluyen datos de
            clientes ni transacciones.
          </span>
        </label>
        <button onClick={guardarDestino} disabled={accion === 'destino'} className="btn-primary">
          Guardar destinatario
        </button>
      </div>

      {/* Probar */}
      <div className="card">
        <h3 className="font-semibold text-slate-800 mb-2">Probar envío</h3>
        <p className="text-sm text-slate-600 mb-3">
          Envía un correo de prueba a la dirección destino para validar que todo funciona.
        </p>
        <button onClick={probar} disabled={accion === 'probar'} className="btn-primary">
          <Send className="h-4 w-4" />
          {accion === 'probar' ? 'Enviando…' : 'Enviar correo de prueba'}
        </button>
      </div>

      {mensaje && (
        <div
          className={`rounded-lg px-4 py-3 text-sm flex items-start gap-2 ${
            mensaje.tipo === 'ok' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'
          }`}
        >
          {mensaje.tipo === 'error' && <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />}
          <span>{mensaje.texto}</span>
        </div>
      )}
    </div>
  );
}
